import React, { useState, useMemo, useEffect, useCallback } from "react";
import { dbFetchLeads, dbSaveLead, dbDeleteLead, dbLogActivity, dbSaveSubscriber, dbFetchSubscribers } from "./supabase";

// ── Config ───────────────────────────────────────────────────────────────────
const SOURCES   = ["Direct Call", "WhatsApp", "Ads", "Marketplace", "Referral", "Instagram"];
const PIPELINE  = ["New", "Quote Sent", "Interested", "Trial", "Converted"]; // ordered stages
const LOST       = "Lost";
const ALL_STATUSES = [...PIPELINE, LOST];
const INTERESTS = ["", "Veg", "Non-Veg", "Vegan", "Jain"];

// Map any legacy/unknown status onto the new pipeline so old leads still render.
// "Qualified" and "Contacted" (old early-stage names) now land on "Quote Sent" —
// the nearest equivalent in the new order. Re-check these manually once if it matters.
const LEGACY_STATUS = { Qualified: "Quote Sent", Contacted: "Quote Sent" };
const normalizeStatus = (s) =>
  ALL_STATUSES.includes(s) ? s : (LEGACY_STATUS[s] || "New");

const STATUS_STYLE = {
  "New":        { bg: "#E6F1FB", color: "#0C447C" },
  "Quote Sent": { bg: "#EEEDFE", color: "#3C3489" },
  "Interested": { bg: "#FAEEDA", color: "#633806" },
  "Trial":      { bg: "#EAF3DE", color: "#085041" },
  "Converted":  { bg: "#DCF1E6", color: "#1D7A52" },
  "Lost":       { bg: "#F4ECEC", color: "#8A4A4A" },
};

// Solid accent per stage — used for column headers & funnel.
const STAGE_ACCENT = {
  "New": "#0C447C", "Quote Sent": "#4A40A8", "Interested": "#B5710A",
  "Trial": "#0B7A63", "Converted": "#1D7A52",
};

const SOURCE_ICON = {
  "Direct Call": "📞", "WhatsApp": "💬", "Ads": "📣",
  "Marketplace": "🛒", "Referral": "◎", "Instagram": "📷",
};

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const isOverdue = (followUp) => followUp && new Date(followUp) < new Date(new Date().toISOString().slice(0,10));
const isDueToday = (followUp) => followUp === new Date().toISOString().slice(0,10);

const emptyLead = () => ({
  id: Date.now().toString(),
  name: "", phone: "", email: "",
  source: "Direct Call", status: "New",
  mealInterest: "", area: "", notes: "",
  followUp: "", convertedId: null,
  dropReason: "", droppedFromStage: null,
});


// ── Convert to Subscriber Modal ──────────────────────────────────────────────
function ConvertModal({ lead, onConfirm, onClose }) {
  const INITIAL_AREAS   = ["Anna Nagar","T Nagar","Velachery","Adyar","Nungambakkam","Porur","Chromepet","Mylapore","Sholinganallur","Ambattur"];
  const DAY_INDEX       = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
  const MEAL_SLOTS      = ["Breakfast","Lunch","Dinner"];
  const ALL_DAYS        = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const SLOT_COLORS     = { Breakfast:"#BA7517", Lunch:"#1D9E75", Dinner:"#534AB7" };

  const calcEndDate = (start, totalDays, excludedDays) => {
    if (!start || !totalDays) return "";
    const excSet = new Set((excludedDays||[]).map(d => DAY_INDEX[d]));
    const d = new Date(start); let counted = 0;
    while (counted < totalDays) {
      if (!excSet.has(d.getDay())) counted++;
      if (counted < totalDays) d.setDate(d.getDate()+1);
    }
    return d.toISOString().slice(0,10);
  };

  const today = new Date().toISOString().slice(0,10);
  const isTrial = lead._targetStage === "Trial";
  const editingExisting = !!lead._editingExisting;
  const [form, setForm] = React.useState(
    editingExisting
      ? {
          // Editing a real subscriber — preserve everything they already have.
          id: lead.id,
          name: lead.name || "", phone: lead.phone || "", email: lead.email || "",
          plan: lead.plan || "Weekly", type: lead.type || "Veg", amount: Number(lead.amount) || 0,
          status: lead.status || "Active",
          startDate: lead.startDate || today,
          endDate: lead.endDate || calcEndDate(lead.startDate || today, lead.totalDays || 7, lead.excludedDays || []),
          totalDays: lead.totalDays || 7,
          excludedDays: lead.excludedDays || [],
          mealSlots: lead.mealSlots && lead.mealSlots.length ? lead.mealSlots : ["Lunch"],
          slotRiders: lead.slotRiders || {}, pausePeriods: lead.pausePeriods || [],
          deliveryArea: lead.deliveryArea || lead.area || INITIAL_AREAS[0],
          notes: lead.notes || "", pauseToken: lead.pauseToken || null,
        }
      : {
          id: Date.now().toString(),
          name: lead.name || "", phone: lead.phone || "", email: lead.email || "",
          plan: isTrial ? "Trial" : "Weekly", type: lead.mealInterest || "Veg", amount: 0,
          status: "Active", startDate: today, endDate: calcEndDate(today, isTrial ? 3 : 7, []),
          totalDays: isTrial ? 3 : 7, excludedDays: [], mealSlots: ["Lunch"],
          slotRiders: {}, pausePeriods: [],
          deliveryArea: lead.area || INITIAL_AREAS[0], notes: "",
        }
  );
  const [saving, setSaving] = React.useState(false);

  const set = (k,v) => setForm(f => {
    const u = {...f, [k]:v};
    if (["totalDays","startDate","excludedDays"].includes(k))
      u.endDate = calcEndDate(u.startDate, u.totalDays, u.excludedDays);
    if (k === "plan") {
      u.totalDays = v==="Weekly"?7:v==="Trial"?3:30;
      u.endDate   = calcEndDate(u.startDate, u.totalDays, u.excludedDays);
    }
    return u;
  });

  const toggleDay  = d => set("excludedDays", form.excludedDays.includes(d)?form.excludedDays.filter(x=>x!==d):[...form.excludedDays,d]);
  const toggleSlot = s => set("mealSlots", form.mealSlots.includes(s)?form.mealSlots.filter(x=>x!==s):[...form.mealSlots,s]);

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Name is required"); return; }
    setSaving(true);
    try {
      await dbSaveSubscriber(form, lead.userId || "");
      await dbLogActivity(lead.userId||"","◎","#1D9E75", editingExisting
        ? `${form.name} (existing subscriber) updated from lead`
        : `${form.name} converted from lead and added as subscriber (${isTrial ? "Trial" : "Converted"})`);
      await onConfirm();
    } catch(e) { alert("Save failed: "+e.message); setSaving(false); }
  };

  const inp = (label, key, type="text", full=false) => (
    <div key={key} style={{gridColumn:full?"1/-1":"auto"}}>
      <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>{label}</label>
      <input type={type} value={form[key]||""} onChange={e=>set(key,type==="number"?Number(e.target.value):e.target.value)}
        style={{width:"100%",padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",outline:"none",boxSizing:"border-box"}}/>
    </div>
  );

  const sel = (label, key, opts) => (
    <div key={key}>
      <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>{label}</label>
      <select value={form[key]||""} onChange={e=>set(key,e.target.value)}
        style={{width:"100%",padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",outline:"none"}}>
        {opts.map(o=><option key={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1100,fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
      <div className="modal-box" style={{background:"#FFF",borderRadius:16,padding:"2rem"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1.25rem"}}>
          <div>
            <h3 style={{fontSize:16,fontWeight:600,margin:0}}>{editingExisting ? "Edit subscriber" : isTrial ? "Start trial" : "Convert to subscriber"}</h3>
            <p style={{fontSize:12,color:"#78716C",margin:"4px 0 0"}}>{editingExisting ? "This phone already exists — editing the existing subscriber" : isTrial ? "Set up a short trial for this lead" : "Review and complete subscription details"}</p>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#A8A29E"}}>✕</button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          {inp("Full name *","name","text",true)}
          {inp("Phone","phone","tel")}
          {inp("Email","email","email")}
          {sel("Plan","plan",["Weekly","Monthly","Trial"])}
          {sel("Meal type","type",["Veg","Non-Veg","Vegan","Jain"])}
          {inp("Amount (₹)","amount","number")}
          {sel("Status","status",["Active","Pending","Paused","Overdue","Inactive"])}
          {sel("Delivery area","deliveryArea",INITIAL_AREAS)}
          {inp("Start date","startDate","date")}

          <div>
            <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>
              Delivery days
              <span style={{color:"#A8A29E",marginLeft:6,fontSize:11}}>
                (default: {form.plan==="Weekly"?7:form.plan==="Trial"?3:30})
              </span>
            </label>
            <input type="number" min={1} max={365} value={form.totalDays}
              onChange={e=>set("totalDays",Number(e.target.value))}
              style={{width:"100%",padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",outline:"none",boxSizing:"border-box"}}/>
          </div>

          <div style={{gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:8}}>Meal slots</label>
            <div style={{display:"flex",gap:8}}>
              {MEAL_SLOTS.map(sl=>{
                const active=(form.mealSlots||[]).includes(sl);
                return(<button key={sl} onClick={()=>toggleSlot(sl)} style={{padding:"7px 18px",borderRadius:8,fontSize:13,cursor:"pointer",border:`0.5px solid ${active?SLOT_COLORS[sl]:"#E7E5E0"}`,background:active?`${SLOT_COLORS[sl]}18`:"#FFF",color:active?SLOT_COLORS[sl]:"#78716C",fontWeight:active?500:400}}>
                  {active?"✓ ":""}{sl}
                </button>);
              })}
            </div>
          </div>

          <div style={{gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:8}}>Exclude days</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {ALL_DAYS.map(d=>{
                const exc=(form.excludedDays||[]).includes(d);
                return(<button key={d} onClick={()=>toggleDay(d)} style={{padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${exc?"#A32D2D":"#E7E5E0"}`,background:exc?"#FCEBEB":"#FFF",color:exc?"#A32D2D":"#78716C"}}>
                  {exc?"✕ ":""}{d}
                </button>);
              })}
            </div>
          </div>

          <div style={{gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>
              End date (auto-calculated)
            </label>
            <div style={{padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #F3F2EE",background:"#F7F6F2",color:"#44403C"}}>
              {form.endDate || "Set start date and days above"}
            </div>
          </div>

          <div style={{gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>Notes</label>
            <input value={form.notes||""} onChange={e=>set("notes",e.target.value)}
              style={{width:"100%",padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",outline:"none",boxSizing:"border-box"}}/>
          </div>
        </div>

        <div style={{display:"flex",gap:10,marginTop:"1.5rem"}}>
          <button onClick={handleSave} disabled={saving}
            style={{flex:1,padding:"10px",fontSize:14,fontWeight:500,borderRadius:8,border:"none",background:saving?"#78716C":"#1D9E75",color:"#FFF",cursor:saving?"default":"pointer"}}>
            {saving?"Saving…":(editingExisting?"✓ Save changes":"✓ Save as subscriber")}
          </button>
          <button onClick={onClose} style={{padding:"10px 20px",fontSize:14,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer"}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Drop (mark lost) Modal — captures the reason + originating stage ──────────
function DropModal({ lead, onConfirm, onClose }) {
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const fromStage = normalizeStatus(lead.status === LOST ? (lead.droppedFromStage || "New") : lead.status);

  const handle = async () => {
    setSaving(true);
    try { await onConfirm(reason.trim()); }
    catch (e) { alert("Failed: " + e.message); setSaving(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1100,fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
      <div style={{background:"#FFF",borderRadius:16,padding:"1.75rem",width:"min(420px,92vw)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
          <div>
            <h3 style={{fontSize:16,fontWeight:600,margin:0}}>Drop lead</h3>
            <p style={{fontSize:12,color:"#78716C",margin:"4px 0 0"}}>{lead.name}</p>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#A8A29E"}}>✕</button>
        </div>

        <div style={{fontSize:12,color:"#78716C",marginBottom:12}}>
          Dropping from{" "}
          <span style={{fontWeight:500,padding:"2px 8px",borderRadius:20,background:STATUS_STYLE[fromStage]?.bg,color:STATUS_STYLE[fromStage]?.color}}>
            {fromStage}
          </span>
        </div>

        <label style={{display:"block",fontSize:12,color:"#78716C",marginBottom:5}}>Reason for dropping *</label>
        <textarea autoFocus value={reason} onChange={e=>setReason(e.target.value)} rows={3}
          placeholder="e.g. Out of delivery area, price too high, went silent…"
          style={{width:"100%",padding:"8px 10px",fontSize:13,borderRadius:8,border:"0.5px solid #E7E5E0",outline:"none",resize:"vertical",boxSizing:"border-box"}}/>

        <div style={{display:"flex",gap:10,marginTop:"1.25rem"}}>
          <button onClick={handle} disabled={saving || !reason.trim()}
            style={{flex:1,padding:"10px",fontSize:14,fontWeight:500,borderRadius:8,border:"none",background:(saving||!reason.trim())?"#D6D3CE":"#A32D2D",color:"#FFF",cursor:(saving||!reason.trim())?"default":"pointer"}}>
            {saving ? "Saving…" : "Drop lead"}
          </button>
          <button onClick={onClose} style={{padding:"10px 20px",fontSize:14,borderRadius:8,border:"0.5px solid #E7E5E0",background:"#FFF",cursor:"pointer"}}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Main ─────────────────────────────────────────────────────────────────────
export default function Leads({ userId = "default", onConvert }) {
  const [leads,        setLeads]        = useState([]);
  const [subscribers,  setSubscribers]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [filterSource, setFilterSource] = useState("All");
  const [view,         setView]         = useState("pipeline"); // "pipeline" | "table"
  const [showForm,     setShowForm]     = useState(false);
  const [editData,     setEditData]     = useState(null);
  const [selected,     setSelected]     = useState(null);
  const [convertLead,  setConvertLead]  = useState(null);
  const [dropTarget,   setDropTarget]   = useState(null);       // lead pending a drop reason
  const [dragId,       setDragId]       = useState(null);
  const [dragOver,     setDragOver]     = useState(null);
  // table-only
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy,       setSortBy]       = useState("createdAt");
  const [sortDir,      setSortDir]      = useState("desc");

  useEffect(() => {
    dbFetchLeads(userId).then(data => { setLeads(data); setLoading(false); }).catch(() => setLoading(false));
    dbFetchSubscribers(userId).then(setSubscribers).catch(() => {});
  }, [userId]);

  const stageOf = (l) => (l.status === LOST ? LOST : normalizeStatus(l.status));

  const droppedLeads = useMemo(() =>
    leads.filter(l => stageOf(l) === LOST)
         .sort((a, b) => new Date(b.droppedAt || 0) - new Date(a.droppedAt || 0)),
  [leads]);

  const droppedRows = useMemo(() => droppedLeads.filter(l => {
    const q = search.toLowerCase();
    if (q && !l.name.toLowerCase().includes(q) && !(l.phone||"").includes(q)) return false;
    if (filterSource !== "All" && l.source !== filterSource) return false;
    return true;
  }), [droppedLeads, search, filterSource]);

  // Search + source filter (applies to both views).
  const visible = useMemo(() => leads.filter(l => {
    const q = search.toLowerCase();
    if (q && !l.name.toLowerCase().includes(q) && !(l.phone||"").includes(q)) return false;
    if (filterSource !== "All" && l.source !== filterSource) return false;
    return true;
  }), [leads, search, filterSource]);

  // Table view — extra status filter + sorting.
  const tableRows = useMemo(() => {
    let list = visible.filter(l => filterStatus === "All" || stageOf(l) === filterStatus);
    list = [...list].sort((a, b) => {
      let va = a[sortBy] || "", vb = b[sortBy] || "";
      if (typeof va === "string") { va = va.toLowerCase(); vb = vb.toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [visible, filterStatus, sortBy, sortDir]);

  // Cards grouped by stage for the board.
  const byStage = useMemo(() => {
    const g = Object.fromEntries([...PIPELINE, LOST].map(s => [s, []]));
    visible.forEach(l => { g[stageOf(l)]?.push(l); });
    return g;
  }, [visible]);

  // Funnel math — counts how far each lead progressed (converted leads count toward
  // every earlier stage; dropped leads count up to the stage they were dropped from).
  const funnel = useMemo(() => {
    const furthest = (l) => {
      if (stageOf(l) === LOST) {
        const i = PIPELINE.indexOf(normalizeStatus(l.droppedFromStage || "New"));
        return i >= 0 ? i : 0;
      }
      return PIPELINE.indexOf(stageOf(l));
    };
    const reached = PIPELINE.map((_, i) => leads.filter(l => furthest(l) >= i).length);
    const conv    = PIPELINE.map((_, i) => i === 0 ? null
      : (reached[i-1] === 0 ? 0 : Math.round((reached[i] / reached[i-1]) * 100)));
    const lostCount  = leads.filter(l => stageOf(l) === LOST).length;
    const overall    = reached[0] === 0 ? 0 : Math.round((reached[PIPELINE.length-1] / reached[0]) * 100);
    return { reached, conv, lostCount, overall, total: leads.length };
  }, [leads]);

  const followUps = useMemo(() => ({
    overdue:  leads.filter(l => isOverdue(l.followUp)  && stageOf(l) !== "Converted" && stageOf(l) !== LOST).length,
    dueToday: leads.filter(l => isDueToday(l.followUp) && stageOf(l) !== "Converted" && stageOf(l) !== LOST).length,
  }), [leads]);

  const openAdd  = () => { setEditData(emptyLead()); setShowForm(true); setSelected(null); };
  const openEdit = (l) => { setEditData({ ...l }); setShowForm(true); setSelected(null); };

  const saveLead = useCallback(async () => {
    if (!editData.name.trim())  { alert("Full name is required.");      return; }
    if (!editData.phone.trim()) { alert("Contact number is required."); return; }
    const isNew = !leads.find(l => l.id === editData.id);
    try {
      const saved = await dbSaveLead(editData, userId);
      setLeads(prev => isNew ? [saved, ...prev] : prev.map(l => l.id === saved.id ? saved : l));
      if (isNew) await dbLogActivity(userId, "◎", "#0C447C", `New lead: ${saved.name} (${saved.source})`);
      setShowForm(false); setEditData(null);
    } catch (err) { alert("Save failed: " + err.message); }
  }, [editData, leads, userId]);

  const deleteLead = useCallback(async (id) => {
    try {
      await dbDeleteLead(id);
      setLeads(prev => prev.filter(l => l.id !== id));
      setSelected(null);
    } catch (err) { alert("Delete failed: " + err.message); }
  }, []);

  // Persist a patch onto a lead and update local state.
  const patchLead = useCallback(async (lead, patch, logText, logColor = "#78716C") => {
    try {
      const saved = await dbSaveLead({ ...lead, ...patch }, userId);
      setLeads(prev => prev.map(x => x.id === saved.id ? saved : x));
      setSelected(prev => prev && prev.id === saved.id ? saved : prev);
      if (logText) await dbLogActivity(userId, "◎", logColor, logText);
      return saved;
    } catch (err) { alert("Update failed: " + err.message); }
  }, [userId]);

  const restoreToStage = (lead, stage) =>
    patchLead(lead, { status: stage, dropReason: "", droppedFromStage: null },
      `${lead.name} restored to ${stage}`, STAGE_ACCENT[stage] || "#78716C");

  const confirmDrop = async (reason) => {
    const lead = dropTarget;
    const fromStage = normalizeStatus(lead.status === LOST ? (lead.droppedFromStage || "New") : lead.status);
    await patchLead(lead,
      { status: LOST, droppedFromStage: fromStage, dropReason: reason, droppedAt: new Date().toISOString() },
      `${lead.name} dropped from ${fromStage}${reason ? " — " + reason : ""}`, "#A32D2D");
    setDropTarget(null);
  };

  // Move a card to a stage. Trial & Converted open the subscriber flow.
  const moveToStage = (id, stage) => {
    const l = leads.find(x => x.id === id);
    if (!l) return;
    if (stageOf(l) === LOST)                          { restoreToStage(l, stage); return; }
    if (stageOf(l) === stage)                         return;
    if (stage === "Trial" || stage === "Converted")   { startConvert(l, stage); return; }
    patchLead(l, { status: stage }, `${l.name} → ${stage}`, STAGE_ACCENT[stage] || "#78716C");
  };

  const sendWa = (lead) => {
    const msg = `Hi ${lead.name.split(" ")[0]}! 👋\n\nThank you for your interest in our meal subscription service.\n\nWe'd love to tell you more about our plans. When would be a good time to talk?\n\nLooking forward to hearing from you! 😊`;
    const phone = (lead.phone || "").replace(/\D/g, "");
    const fullPhone = phone.startsWith("91") ? phone : `91${phone}`;
    window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const last10 = (p) => (p || "").replace(/\D/g, "").slice(-10);

  const startConvert = (lead, targetStage = "Converted") => {
    const leadDigits = last10(lead.phone);
    const existing = leadDigits
      ? subscribers.find(s => last10(s.phone) === leadDigits)
      : null;

    if (existing) {
      // Edit the existing subscriber — carry their id + real fields so the
      // upsert updates that row instead of creating a duplicate.
      setConvertLead({
        ...existing,
        userId,
        _targetStage: targetStage,
        _editingExisting: true,
        _leadId: lead.id,
        // keep the lead's interest/area only if the subscriber has none
        mealInterest: existing.type || lead.mealInterest || "",
        area: existing.deliveryArea || lead.area || "",
      });
    } else {
      setConvertLead({ ...lead, userId, _targetStage: targetStage });
    }
  };
  const finishConvert = async (lead, targetStage = "Converted") => {
    const leadId = lead._leadId || lead.id;
    const target = leads.find(x => x.id === leadId) || lead;
    await patchLead(target, { status: targetStage }, `${target.name} lead → ${targetStage}`,
      STAGE_ACCENT[targetStage] || "#1D7A52");
    // refresh subscriber cache so a later convert sees this one too
    dbFetchSubscribers(userId).then(setSubscribers).catch(() => {});
    setConvertLead(null); setSelected(null);
  };

  const SortIcon = ({ col }) => (
    <span style={{ fontSize: 10, marginLeft: 4, opacity: sortBy === col ? 1 : 0.3 }}>
      {sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
    </span>
  );
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  if (loading) {
    return (
      <div className="page-wrap" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#78716C", fontSize: 13 }}>Loading leads…</div>
      </div>
    );
  }

  const convColor = (v) => v == null ? "#A8A29E" : v >= 66 ? "#1D7A52" : v >= 33 ? "#B5710A" : "#A32D2D";

  return (
    <div className="page-wrap" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh" }}>

      {/* Header */}
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>Leads pipeline</h1>
          <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>
            {funnel.total} total · {funnel.reached[PIPELINE.length-1]} converted · {funnel.lostCount} dropped
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "inline-flex", background: "#FFF", border: "0.5px solid #E7E5E0", borderRadius: 8, padding: 3 }}>
            {["pipeline", "table"].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: "6px 14px", fontSize: 13, borderRadius: 6, border: "none", cursor: "pointer", textTransform: "capitalize",
                  background: view === v ? "#1C1917" : "transparent", color: view === v ? "#FFF" : "#78716C", fontWeight: view === v ? 500 : 400 }}>
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => setView(view === "dropped" ? "pipeline" : "dropped")}
            style={{ background: view === "dropped" ? "#A32D2D" : "#FBF3F3", color: view === "dropped" ? "#FFF" : "#A32D2D", border: "0.5px solid #F0D9D9", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}>
            Dropped {funnel.lostCount > 0 ? `(${funnel.lostCount})` : ""}
          </button>
          <button onClick={openAdd} style={{ background: "#1C1917", color: "#FFF", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap" }}>
            + Add lead
          </button>
        </div>
      </div>

      {/* Funnel strip — cumulative reach + conversion between stages */}
      <div style={{ background: "#FFF", border: "0.5px solid #E7E5E0", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#78716C" }}>Funnel — leads that reached each stage, and % advancing</span>
          <span style={{ fontSize: 12, color: "#78716C" }}>
            Overall New → Converted: <strong style={{ color: convColor(funnel.overall) }}>{funnel.overall}%</strong>
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "stretch", gap: 0, overflowX: "auto", paddingBottom: 4 }}>
          {PIPELINE.map((stage, i) => (
            <React.Fragment key={stage}>
              {i > 0 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 60, padding: "0 6px" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: convColor(funnel.conv[i]) }}>{funnel.conv[i]}%</span>
                  <span style={{ fontSize: 16, color: "#D6D3CE", lineHeight: 1 }}>→</span>
                </div>
              )}
              <div style={{ flex: "1 1 0", minWidth: 110, background: STATUS_STYLE[stage].bg, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: STAGE_ACCENT[stage], display: "inline-block" }} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: STATUS_STYLE[stage].color }}>{stage}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, color: "#1C1917", lineHeight: 1 }}>{funnel.reached[i]}</div>
                <div style={{ fontSize: 10, color: "#78716C", marginTop: 2 }}>
                  {byStage[stage].length} here now
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Follow-up alerts */}
      {(followUps.overdue > 0 || followUps.dueToday > 0) && (
        <div style={{ display: "flex", gap: 10, marginBottom: "1rem", flexWrap: "wrap" }}>
          {followUps.overdue > 0 && (
            <div style={{ background: "#FCEBEB", border: "0.5px solid #F09595", borderRadius: 10, padding: "8px 14px", fontSize: 13, color: "#A32D2D" }}>
              ⚠ {followUps.overdue} overdue follow-up{followUps.overdue > 1 ? "s" : ""}
            </div>
          )}
          {followUps.dueToday > 0 && (
            <div style={{ background: "#FAEEDA", border: "0.5px solid #EF9F27", borderRadius: 10, padding: "8px 14px", fontSize: 13, color: "#633806" }}>
              📅 {followUps.dueToday} follow-up{followUps.dueToday > 1 ? "s" : ""} due today
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="filter-row" style={{ marginBottom: "1rem", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone…"
          style={{ flex: "1 1 180px", padding: "7px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }} />
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
          style={{ padding: "7px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", outline: "none" }}>
          <option value="All">Source: All</option>
          {SOURCES.map(s => <option key={s}>{s}</option>)}
        </select>
        {view === "table" && (
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: "7px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", outline: "none" }}>
            <option value="All">Stage: All</option>
            {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        )}
      </div>

      <div className="sub-content" style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* ── PIPELINE (Kanban) view ── */}
          {view === "pipeline" && (
            <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" }}>
              {PIPELINE.map(stage => {
                const cards = byStage[stage] || [];
                const accent = STAGE_ACCENT[stage];
                const hot = dragOver === stage;
                return (
                  <div key={stage}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOver !== stage) setDragOver(stage); }}
                    onDragLeave={() => setDragOver(s => s === stage ? null : s)}
                    onDrop={e => {
                      e.preventDefault();
                      const id = e.dataTransfer.getData("text/plain") || dragId;
                      setDragOver(null); setDragId(null);
                      if (id) moveToStage(id, stage);
                    }}
                    style={{
                      flex: "0 0 244px", width: 244, background: hot ? "#F0EFEA" : "#FBFAF7",
                      border: `1px ${hot ? "dashed" : "solid"} ${hot ? accent : "#EAE8E2"}`,
                      borderRadius: 12, padding: 10, alignSelf: "stretch", minHeight: 120,
                    }}>
                    {/* Column header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "2px 4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, background: accent, display: "inline-block" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1C1917" }}>{stage}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 500, color: "#78716C", background: "#F0EFEA", borderRadius: 20, padding: "1px 8px" }}>{cards.length}</span>
                    </div>

                    {/* Cards */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {cards.length === 0 && (
                        <div style={{ fontSize: 11, color: "#C2BDB5", textAlign: "center", padding: "14px 0" }}>—</div>
                      )}
                      {cards.map(l => {
                        const overdue  = isOverdue(l.followUp) && stage !== "Converted";
                        const dueToday = isDueToday(l.followUp);
                        const dragging = dragId === l.id;
                        return (
                          <div key={l.id}
                            draggable
                            onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", l.id); setDragId(l.id); }}
                            onDragEnd={() => { setDragId(null); setDragOver(null); }}
                            onClick={() => setSelected(selected?.id === l.id ? null : l)}
                            style={{
                              background: "#FFF", border: `0.5px solid ${selected?.id === l.id ? accent : "#E7E5E0"}`,
                              borderLeft: `3px solid ${accent}`, borderRadius: 9, padding: "9px 11px",
                              cursor: "grab", opacity: dragging ? 0.4 : 1, boxShadow: selected?.id === l.id ? "0 1px 6px rgba(0,0,0,0.06)" : "none",
                            }}>
                            <div style={{ fontWeight: 500, fontSize: 13, color: "#1C1917", marginBottom: 2 }}>{l.name}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#A8A29E" }}>
                              <span>{l.phone || "—"}</span>
                              <span>·</span>
                              <span>{SOURCE_ICON[l.source] || "◇"} {l.source}</span>
                            </div>

                            {l.followUp && (
                              <div style={{ marginTop: 6, fontSize: 11, color: overdue ? "#A32D2D" : dueToday ? "#633806" : "#78716C", fontWeight: overdue || dueToday ? 500 : 400 }}>
                                {overdue ? "⚠ " : dueToday ? "📅 " : "🕑 "}{fmt(l.followUp)}
                              </div>
                            )}

                            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                              {l.phone && (
                                <button onClick={e => { e.stopPropagation(); sendWa(l); }} title="WhatsApp"
                                  style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "0.5px solid #BEE9CC", background: "#EAF7EF", color: "#1D7A52", cursor: "pointer" }}>💬</button>
                              )}
                              <button onClick={e => { e.stopPropagation(); setDropTarget(l); }} title="Drop lead"
                                style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "0.5px solid #F0D9D9", background: "#FBF3F3", color: "#A32D2D", cursor: "pointer", marginLeft: "auto" }}>✕ Drop</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── TABLE view ── */}
          {view === "table" && (
            <div className="sub-table-wrap" style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0" }}>
              <table className="sub-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F7F6F2", borderBottom: "0.5px solid #E7E5E0" }}>
                    {[
                      { label: "Name",      col: "name"      },
                      { label: "Source",    col: "source"    },
                      { label: "Interest",  col: "mealInterest" },
                      { label: "Stage",     col: "status"    },
                      { label: "Follow-up", col: "followUp"  },
                      { label: "Added",     col: "createdAt" },
                      { label: "",          col: null        },
                    ].map((h, i) => (
                      <th key={i} onClick={() => h.col && toggleSort(h.col)}
                        style={{ padding: "10px 14px", textAlign: "left", fontWeight: 500, fontSize: 12, color: "#78716C", cursor: h.col ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}>
                        {h.label}{h.col && <SortIcon col={h.col} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "3rem", textAlign: "center", color: "#A8A29E" }}>
                      {leads.length === 0 ? "No leads yet — add your first lead" : "No results found"}
                    </td></tr>
                  )}
                  {tableRows.map((l, i) => {
                    const stg      = stageOf(l);
                    const st       = STATUS_STYLE[stg] || STATUS_STYLE.New;
                    const overdue  = isOverdue(l.followUp) && stg !== "Converted" && stg !== LOST;
                    const dueToday = isDueToday(l.followUp);
                    return (
                      <tr key={l.id} onClick={() => setSelected(selected?.id === l.id ? null : l)}
                        style={{ borderBottom: i < tableRows.length - 1 ? "0.5px solid #F3F2EE" : "none", cursor: "pointer", background: selected?.id === l.id ? "#F7F6F2" : "transparent" }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 500, color: "#1C1917" }}>{l.name}</div>
                          <div style={{ fontSize: 11, color: "#A8A29E" }}>{l.phone}</div>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#78716C", fontSize: 12 }}>
                          <span>{SOURCE_ICON[l.source] || "◇"} {l.source}</span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: l.mealInterest ? "#44403C" : "#A8A29E" }}>
                          {l.mealInterest || "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color }}>
                            {stg}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                          {l.followUp ? (
                            <span style={{ color: overdue ? "#A32D2D" : dueToday ? "#633806" : "#44403C", fontWeight: overdue || dueToday ? 500 : 400 }}>
                              {overdue ? "⚠ " : dueToday ? "📅 " : ""}{fmt(l.followUp)}
                            </span>
                          ) : <span style={{ color: "#A8A29E" }}>—</span>}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 11, color: "#A8A29E" }}>{fmt(l.createdAt)}</td>
                        <td style={{ padding: "10px 10px 10px 0" }}>
                          <button onClick={e => { e.stopPropagation(); openEdit(l); }}
                            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C" }}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── DROPPED view (table) ── */}
          {view === "dropped" && (
            <div className="sub-table-wrap" style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0" }}>
              <table className="sub-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F7F6F2", borderBottom: "0.5px solid #E7E5E0" }}>
                    {["Name", "Mobile", "Source", "Dropped from stage", "Dropped reason", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 500, fontSize: 12, color: "#78716C", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {droppedRows.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: "3rem", textAlign: "center", color: "#A8A29E" }}>
                      {droppedLeads.length === 0 ? "No dropped leads" : "No results found"}
                    </td></tr>
                  )}
                  {droppedRows.map((l, i) => (
                    <tr key={l.id} onClick={() => setSelected(selected?.id === l.id ? null : l)}
                      style={{ borderBottom: i < droppedRows.length - 1 ? "0.5px solid #F3F2EE" : "none", cursor: "pointer", background: selected?.id === l.id ? "#F7F6F2" : "transparent" }}>
                      <td style={{ padding: "10px 14px", fontWeight: 500, color: "#1C1917" }}>{l.name}</td>
                      <td style={{ padding: "10px 14px", color: "#44403C" }}>{l.phone || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#78716C", fontSize: 12 }}>{SOURCE_ICON[l.source] || "◇"} {l.source}</td>
                      <td style={{ padding: "10px 14px" }}>
                        {l.droppedFromStage ? (
                          <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: STATUS_STYLE[normalizeStatus(l.droppedFromStage)]?.bg || "#F0EFEA", color: STATUS_STYLE[normalizeStatus(l.droppedFromStage)]?.color || "#78716C" }}>
                            {normalizeStatus(l.droppedFromStage)}
                          </span>
                        ) : <span style={{ color: "#A8A29E" }}>—</span>}
                      </td>
                      <td style={{ padding: "10px 14px", color: "#8A4A4A", maxWidth: 240 }}>{l.dropReason || "—"}</td>
                      <td style={{ padding: "10px 10px 10px 0" }}>
                        <button onClick={e => { e.stopPropagation(); restoreToStage(l, normalizeStatus(l.droppedFromStage || "New")); }}
                          style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C", whiteSpace: "nowrap" }}>
                          ↩ Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="detail-panel">
            <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "1.25rem", position: "sticky", top: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Lead details</div>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#A8A29E" }}>✕</button>
              </div>

              <div style={{ fontWeight: 600, fontSize: 15, color: "#1C1917", marginBottom: 4 }}>{selected.name}</div>
              <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: STATUS_STYLE[stageOf(selected)]?.bg, color: STATUS_STYLE[stageOf(selected)]?.color }}>
                {stageOf(selected)}
              </span>

              <div style={{ marginTop: "1rem" }}>
                {[
                  { label: "Phone",     value: selected.phone    || "—" },
                  { label: "Email",     value: selected.email    || "—" },
                  { label: "Source",    value: `${SOURCE_ICON[selected.source] || ""} ${selected.source}` },
                  { label: "Interest",  value: selected.mealInterest || "—" },
                  { label: "Area",      value: selected.area     || "—" },
                  { label: "Added",     value: fmt(selected.createdAt) },
                  ...(stageOf(selected) === LOST ? [
                    { label: "Dropped from", value: selected.droppedFromStage || "—" },
                    { label: "Drop reason",  value: selected.dropReason || "—" },
                  ] : []),
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "0.5px solid #F3F2EE", fontSize: 13 }}>
                    <span style={{ color: "#78716C" }}>{row.label}</span>
                    <span style={{ color: "#1C1917", textAlign: "right", maxWidth: 160, wordBreak: "break-word" }}>{row.value}</span>
                  </div>
                ))}

                {/* Follow-up date — editable in place, saves on change */}
                <div style={{ padding: "9px 0 7px", borderBottom: "0.5px solid #F3F2EE" }}>
                  <div style={{ fontSize: 13, color: "#78716C", marginBottom: 5 }}>Follow-up date</div>
                  <input type="date" defaultValue={selected.followUp || ""} key={`fu-${selected.id}-${selected.followUp}`}
                    onBlur={e => { if (e.target.value !== (selected.followUp || "")) patchLead(selected, { followUp: e.target.value }); }}
                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
                </div>

                {/* Notes — editable in place, saves on blur */}
                <div style={{ padding: "9px 0 7px" }}>
                  <div style={{ fontSize: 13, color: "#78716C", marginBottom: 5 }}>Notes</div>
                  <textarea defaultValue={selected.notes || ""} key={`notes-${selected.id}-${selected.notes}`} rows={3}
                    onBlur={e => { if (e.target.value !== (selected.notes || "")) patchLead(selected, { notes: e.target.value }); }}
                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, borderRadius: 6, border: "0.5px solid #E7E5E0", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
                </div>
              </div>

              {/* Move stage */}
              <div style={{ marginTop: "1rem" }}>
                <div style={{ fontSize: 12, color: "#78716C", marginBottom: 8 }}>Move to stage</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {PIPELINE.map(s => (
                    <button key={s} onClick={() => moveToStage(selected.id, s)}
                      style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, cursor: "pointer", border: `0.5px solid ${STATUS_STYLE[s].color}`, background: stageOf(selected) === s ? STATUS_STYLE[s].bg : "transparent", color: STATUS_STYLE[s].color, fontWeight: stageOf(selected) === s ? 500 : 400 }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, marginTop: "1rem" }}>
                <button onClick={() => openEdit(selected)} style={{ flex: 1, padding: "8px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#1C1917", color: "#FFF", cursor: "pointer", fontWeight: 500 }}>Edit</button>
                <button onClick={() => deleteLead(selected.id)} style={{ padding: "8px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #F09595", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}>Delete</button>
              </div>

              {selected.phone && (
                <button onClick={() => sendWa(selected)}
                  style={{ width: "100%", marginTop: 8, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#25D366", color: "#FFF", cursor: "pointer" }}>
                  💬 Send WhatsApp message
                </button>
              )}

              {stageOf(selected) !== "Trial" && stageOf(selected) !== "Converted" && stageOf(selected) !== LOST && (
                <button onClick={() => startConvert(selected, "Trial")}
                  style={{ width: "100%", marginTop: 8, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "0.5px solid #0B7A63", background: "#FFF", color: "#0B7A63", cursor: "pointer" }}>
                  🍱 Start trial
                </button>
              )}

              {stageOf(selected) !== "Converted" && stageOf(selected) !== LOST && (
                <button onClick={() => startConvert(selected, "Converted")}
                  style={{ width: "100%", marginTop: 8, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#1D9E75", color: "#FFF", cursor: "pointer" }}>
                  ✓ Convert to subscriber
                </button>
              )}

              {stageOf(selected) === LOST ? (
                <button onClick={() => restoreToStage(selected, normalizeStatus(selected.droppedFromStage || "New"))}
                  style={{ width: "100%", marginTop: 8, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", color: "#44403C", cursor: "pointer" }}>
                  ↩ Restore lead
                </button>
              ) : (
                <button onClick={() => setDropTarget(selected)}
                  style={{ width: "100%", marginTop: 8, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "0.5px solid #F0D9D9", background: "#FBF3F3", color: "#A32D2D", cursor: "pointer" }}>
                  ✕ Drop lead
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showForm && editData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="modal-box" style={{ background: "#FFF", borderRadius: 16, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{leads.find(l => l.id === editData.id) ? "Edit lead" : "Add lead"}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "Full name *",      key: "name",     type: "text", full: true },
                { label: "Contact number *", key: "phone",    type: "tel",  mandatory: true },
                { label: "Email",            key: "email",    type: "email" },
                { label: "Area",             key: "area",     type: "text"  },
                { label: "Follow-up date",   key: "followUp", type: "date"  },
              ].map(f => (
                <div key={f.key} style={{ gridColumn: f.full ? "1/-1" : "auto" }}>
                  <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>{f.label}</label>
                  <input type={f.type} value={editData[f.key] || ""}
                    onChange={e => setEditData(d => ({ ...d, [f.key]: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: `0.5px solid ${f.mandatory && !editData[f.key] ? "#F09595" : "#E7E5E0"}`, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}

              {[
                { label: "Source",        key: "source",       opts: SOURCES      },
                { label: "Meal interest", key: "mealInterest", opts: INTERESTS    },
                { label: "Stage",         key: "status",       opts: ALL_STATUSES },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>{f.label}</label>
                  <select value={editData[f.key] || ""} onChange={e => setEditData(d => ({ ...d, [f.key]: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                    {f.opts.map(o => <option key={o} value={o}>{o || "— Not specified —"}</option>)}
                  </select>
                </div>
              ))}

              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Notes</label>
                <textarea value={editData.notes || ""} onChange={e => setEditData(d => ({ ...d, notes: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button onClick={saveLead} style={{ flex: 1, padding: "10px", fontSize: 14, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>
                Save lead
              </button>
              <button onClick={() => setShowForm(false)} style={{ padding: "10px 20px", fontSize: 14, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Subscriber / Start Trial modal */}
      {convertLead && (
        <ConvertModal
          lead={convertLead}
          onConfirm={() => finishConvert(convertLead, convertLead._targetStage)}
          onClose={() => setConvertLead(null)}
        />
      )}

      {/* Drop (mark lost) modal */}
      {dropTarget && (
        <DropModal
          lead={dropTarget}
          onConfirm={confirmDrop}
          onClose={() => setDropTarget(null)}
        />
      )}
    </div>
  );
}
